import React, { useState } from "react";
import { Star, MapPin, Clock, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import "./ServiceDetail.css";
import { Link } from "react-router-dom";
import OverviewSection from "./OverviewSection";
import SuccessTimeline from "./SuccessTimeline";
import TechStackSection from "./TechStackSection";
import FeatureTab from "./FeatureTab";
import BenefitSection from "./BenefitSection";

function ServiceDetailSection({
  service, // { title, description, image, features, benefits, process, techStack }
  extension, // optional additional data
  officeLocations,
  caseStudies,
}) {
  const [activeTab, setActiveTab] = useState("overview");

  return (
    <div className="service-detail-page">
      {/* Hero Section */}
      <motion.section
        className="hero-section-service position-relative overflow-hidden py-5 pt-7"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container mt-5">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <motion.h1
                className="display-4 fw-bold gradient-text-blue mb-4"
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.7 }}
              >
                {service.title}
              </motion.h1>
              <motion.p
                className="lead text-secondary mb-4 fw-500"
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.7 }}
              >
                {service.description}
              </motion.p>
              <div>
                <Link
                  to={"/contact"}
                  className="btn btn-primary btn-lg me-3 position-relative z-1"
                >
                  Get Started <ArrowRight size={20} />
                </Link>
              </div>
            </div>
            <div className="col-lg-6 position-relative">
              <motion.img
                src={service.image}
                alt={service.title}
                className="img-fluid rounded-5 shadow-sm"
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.8 }}
              />
            </div>
          </div>
        </div>
      </motion.section>

      {/* Tabs Navigation */}
      <motion.section
        className="border-top border-bottom bg-white nav-tabs-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="container">
          <ul
            className="nav nav-pills justify-content-center flex-nowrap py-3 mb-0"
            role="tablist"
          >
            {[
              { id: "overview", label: "Overview" },
              { id: "features", label: "Key Features" },
              { id: "benefits", label: "Benefits" },
              { id: "process", label: "Process" },
              { id: "techStack", label: "Tech Stack" },
              // { id: "offices", label: "Our Offices" },
              // { id: "caseStudies", label: "Case Studies" },
            ].map((tab) => (
              <li className="nav-item" key={tab.id}>
                <button
                  className={`nav-link ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                  role="tab"
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </motion.section>

      {/* Tab Content */}
      <motion.section
        className="py-5"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container">
          {/* Overview Tab */}
          {activeTab === "overview" && <OverviewSection service={service} />}

          {/* Features Tab */}
          {activeTab === "features" && extension.keyFeatures && (
            <FeatureTab feature={extension.keyFeatures} />
          )}

          {/* Benefits Tab */}
          {activeTab === "benefits" && extension && (
            <BenefitSection benefits={extension.benefits} />
          )}

          {/* Process Tab */}
          {activeTab === "process" && extension && (
            <SuccessTimeline extension={extension} />
          )}

          {/* Tech Stack Tab */}
          {activeTab === "techStack" && extension && (
            <TechStackSection techStack={extension.techStack} />
          )}

          {/* Offices Tab */}
          {activeTab === "offices" && (
            <div className="row gy-4">
              {officeLocations.map((office, idx) => (
                <motion.div
                  className="col-lg-4"
                  key={idx}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="card h-100 shadow-sm overflow-hidden">
                    <img
                      src={office.image}
                      className="card-img-top"
                      alt={`${office.city}, ${office.country}`}
                    />
                    <div className="card-body">
                      <h5 className="card-title d-flex align-items-center">
                        <MapPin size={20} className="me-2 text-primary" />
                        {office.city}
                      </h5>
                      <p className="card-text">{office.address}</p>
                      <p className="small text-muted">
                        <Clock size={14} className="me-1" />
                        {office.timezone}
                      </p>
                      <p className="small text-muted">{office.email}</p>
                      <div className="mb-2 small">
                        <strong>Specializations:</strong>{" "}
                        {office.services.join(", ")}
                      </div>
                      <button className="btn btn-primary w-100">
                        Contact {office.city} Office
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Case Studies Tab */}
          {activeTab === "caseStudies" && (
            <div>
              {caseStudies && caseStudies.length > 0 ? (
                <div className="row gy-4">
                  {caseStudies.map((cs, idx) => (
                    <motion.div
                      className="col-lg-6"
                      key={idx}
                      initial={{ opacity: 0, y: 50 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                    >
                      <div className="card h-100 shadow-sm overflow-hidden">
                        <img
                          src={cs.image}
                          className="card-img-top"
                          alt={cs.title}
                        />
                        <div className="card-body">
                          <h5 className="card-title">{cs.title}</h5>
                          <p className="card-text">
                            <strong>Client:</strong> {cs.client}
                          </p>
                          <p className="card-text">
                            <strong>Challenge:</strong> {cs.challenge}
                          </p>
                          <p className="card-text">
                            <strong>Solution:</strong> {cs.solution}
                          </p>
                          <div className="d-flex justify-content-between align-items-center border-top pt-3">
                            <small className="text-muted">
                              <Clock size={14} className="me-1" />
                              {cs.timeline}
                            </small>
                            <button className="btn btn-outline-success btn-sm">
                              View Details{" "}
                              <ArrowRight size={14} className="ms-1" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-5">
                  <div className="mb-3">
                    <div
                      className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center"
                      style={{ width: "4rem", height: "4rem" }}
                    >
                      <Star size={32} className="text-success" />
                    </div>
                  </div>
                  <h5>Case Studies Coming Soon</h5>
                  <p>
                    We're working on detailed case studies for this service.
                    Check back soon for inspiring success stories.
                  </p>
                  <button className="btn btn-success">
                    Contact for Examples
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.section>

      {/* CTA */}
      <motion.section
        className="py-5 text-center cta-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7 }}
      >
        <div className="container">
          <h2 className="mb-3">Ready to Get Started with {service.title}?</h2>
          <p className="mb-4">
            Let’s discuss your specific requirements and create a customized
            solution that delivers results.
          </p>
          <Link to={"/contact"} className="btn btn-light btn-lg me-3">
            Get Free Quote <ArrowRight size={20} />
          </Link>
          {/* <Link to={"/portfolio"} className="btn btn-outline-light btn-lg">
            View Portfolio
          </Link> */}
        </div>
      </motion.section>
    </div>
  );
}

export default ServiceDetailSection;
